import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Grid,
  List,
  Modal,
  Pagination,
  Progress,
  Skeleton,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { HistoryOutlined, ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import PageHeader from "../../components/pageheader/PageHeader";
import {
  RecommendationRun,
  RecommendationRunApi,
} from "../../api/RecommendationRunApi";
import { getTablePagination } from "../../helpers/TablePaginationHelper";
import { isRecommendationRunCancelled } from "../../helpers/RecommendationRunHelper";
import { formatRecommendationMessage } from "../../helpers/RecommendationMessageHelper";
import { useRecommendationRun } from "../../contexts/RecommendationRunContext";
import styles from "./RecommendationRunsHistory.module.scss";

const PAGE_SIZE = 10;

const RecommendationRunsHistory = () => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  // Content column + sidebar is often narrower than the viewport; prefer cards until lg.
  const isCompactList = !screens.lg;
  const { isActive, initialLoading, startingRun, startRun, refreshRun } =
    useRecommendationRun();
  const [searchParams, setSearchParams] = useSearchParams();
  const [runs, setRuns] = useState<RecommendationRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(
    parseInt(searchParams.get("page") || "1"),
  );

  const formatDate = useCallback((value?: string) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }, []);

  const getStatusMeta = useCallback(
    (record: RecommendationRun) => {
      const cancelled = isRecommendationRunCancelled(record);
      const color =
        cancelled || record.status === "FAILED"
          ? "red"
          : record.status === "SUCCESS"
            ? "green"
            : "blue";
      const label =
        record.status === "PENDING"
          ? t("RecommendationRunsHistory-Status-Pending")
          : record.status === "RUNNING"
            ? t("RecommendationRunsHistory-Status-Running")
            : cancelled
              ? t("RecommendationRunsHistory-Status-Cancelled")
              : record.status === "SUCCESS"
                ? t("RecommendationRunsHistory-Status-Success")
                : t("RecommendationRunsHistory-Status-Failed");

      return { colour: color, label, cancelled };
    },
    [t],
  );

  const renderProgress = useCallback(
    (record: RecommendationRun) => {
      const percent = Math.max(
        0,
        Math.min(100, Math.round(record.percent_completed || 0)),
      );
      const cancelled = isRecommendationRunCancelled(record);

      return (
        <div className={styles.progressCell}>
          <Progress
            className={styles.progressBar}
            percent={percent}
            size="small"
            showInfo={false}
            status={
              cancelled || record.status === "FAILED"
                ? "exception"
                : record.status === "SUCCESS"
                  ? "success"
                  : "active"
            }
          />
          <span className={styles.progressValue}>{percent}%</span>
        </div>
      );
    },
    [],
  );

  const renderResults = useCallback(
    (record: RecommendationRun) => (
      <div className={styles.resultsCell}>
        <Tooltip title={t("RecommendationRunsHistory-Segments")}>
          <span>
            {t("RecommendationRunsHistory-MetricShort-Segments")}{" "}
            {record.segments_generated || 0}
          </span>
        </Tooltip>
        <Tooltip title={t("RecommendationRunsHistory-CommercialOpportunities")}>
          <span>
            {t("RecommendationRunsHistory-MetricShort-Opportunities")}{" "}
            {record.commercial_opportunities_generated || 0}
          </span>
        </Tooltip>
        <Tooltip title={t("RecommendationRunsHistory-Rewards")}>
          <span>
            {t("RecommendationRunsHistory-MetricShort-Rewards")}{" "}
            {record.rewards_generated || 0}
          </span>
        </Tooltip>
        <Tooltip title={t("RecommendationRunsHistory-Promotions")}>
          <span>
            {t("RecommendationRunsHistory-MetricShort-Promotions")}{" "}
            {record.promotions_generated || 0}
          </span>
        </Tooltip>
        <Tooltip title={t("RecommendationRunsHistory-Actions")}>
          <span>
            {t("RecommendationRunsHistory-MetricShort-Actions")}{" "}
            {record.actions_generated || 0}
          </span>
        </Tooltip>
      </div>
    ),
    [t],
  );

  const updateURL = useCallback(
    (page: number) => {
      const params = new URLSearchParams(searchParams);
      if (page > 1) params.set("page", page.toString());
      else params.delete("page");
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const fetchHistory = useCallback(async (page: number) => {
    setLoading(true);
    try {
      const response = await RecommendationRunApi.getHistory(page, PAGE_SIZE);
      setRuns(response.recommendationRuns);
      setTotalSize(response.totalElements);
      setActualPage(response.currentPage);
    } catch (error) {
      console.error("Error fetching recommendation runs history:", error);
      message.error(t("RecommendationRunsHistory-LoadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchHistory(actualPage);
  }, [actualPage, fetchHistory]);

  const handlePagination = (page: number) => {
    setActualPage(page);
    updateURL(page);
  };

  const handleStartRecommendations = () => {
    Modal.confirm({
      title: t("NavBar-GenerateRecommendations"),
      content: t("NavBar-GenerateRecommendationsConfirm"),
      okText: t("NavBar-GenerateRecommendationsConfirmOk"),
      cancelText: t("Common-Cancel"),
      onOk: async () => {
        const run = await startRun();
        if (run) {
          setActualPage(1);
          updateURL(1);
          await fetchHistory(1);
          await refreshRun();
        }
      },
    });
  };

  const paginationConfig = getTablePagination(t, {
    total: totalSize,
    pageSize: PAGE_SIZE,
    current: actualPage,
    onChange: handlePagination,
    format: "i18n",
  });

  const columns = useMemo(
    () => [
      {
        title: t("Common-Status"),
        dataIndex: "status",
        key: "status",
        width: 110,
        render: (_: RecommendationRun["status"], record: RecommendationRun) => {
          const { colour, label } = getStatusMeta(record);
          return <Tag color={colour}>{label}</Tag>;
        },
      },
      {
        title: t("RecommendationRunsHistory-Step"),
        dataIndex: "current_step_label",
        key: "current_step_label",
        render: (value?: string, record?: RecommendationRun) => {
          const label =
            formatRecommendationMessage(
              t,
              record?.current_step_label_key,
              record?.current_step_label_params,
              value
            ) || t("RecommendationRunsHistory-StepPending");
          return (
            <div className={styles.stepCell}>
              <Typography.Paragraph
                className={styles.stepLabel}
                ellipsis={{
                  rows: 2,
                  tooltip: label,
                }}
              >
                {label}
              </Typography.Paragraph>
              {record?.current_step_detail && (
                <Typography.Paragraph
                  type="secondary"
                  className={styles.stepDetail}
                  ellipsis={{ rows: 2, tooltip: record.current_step_detail }}
                >
                  {record.current_step_detail}
                </Typography.Paragraph>
              )}
            </div>
          );
        },
      },
      {
        title: t("RecommendationRunsHistory-Progress"),
        key: "progress",
        width: 120,
        render: (_: unknown, record: RecommendationRun) => renderProgress(record),
      },
      {
        title: t("RecommendationRunsHistory-Results"),
        key: "results",
        width: 160,
        render: (_: unknown, record: RecommendationRun) => renderResults(record),
      },
      {
        title: t("RecommendationRunsHistory-Time"),
        key: "time",
        width: 118,
        render: (_: unknown, record: RecommendationRun) => (
          <div className={styles.timeCell}>
            <div className={styles.timeBlock}>
              <span className={styles.timeLabel}>
                {t("RecommendationRunsHistory-StartedAtShort")}
              </span>
              <span className={styles.timeValue}>
                {formatDate(record.started_at)}
              </span>
            </div>
            <div className={styles.timeBlock}>
              <span className={styles.timeLabel}>
                {t("RecommendationRunsHistory-FinishedAtShort")}
              </span>
              <span className={styles.timeValue}>
                {formatDate(record.finished_at)}
              </span>
            </div>
          </div>
        ),
      },
      {
        title: t("RecommendationRunsHistory-Error"),
        dataIndex: "error_message",
        key: "error_message",
        ellipsis: true,
        render: (value?: string) =>
          value ? (
            <Typography.Text
              className={styles.errorText}
              ellipsis={{ tooltip: value }}
            >
              {value}
            </Typography.Text>
          ) : (
            "-"
          ),
      },
    ],
    [formatDate, getStatusMeta, renderProgress, renderResults, t],
  );

  const renderRunCard = (record: RecommendationRun) => {
    const { colour, label } = getStatusMeta(record);
    const stepLabel =
      formatRecommendationMessage(
        t,
        record.current_step_label_key,
        record.current_step_label_params,
        record.current_step_label
      ) || t("RecommendationRunsHistory-StepPending");

    return (
      <article className={styles.runCard}>
        <div className={styles.cardHeader}>
          <Tag color={colour}>{label}</Tag>
          <span className={styles.cardDate}>{formatDate(record.created_at)}</span>
        </div>

        <Typography.Text className={styles.cardStep} ellipsis={{ tooltip: stepLabel }}>
          {stepLabel}
        </Typography.Text>
        {record.current_step_detail && (
          <Typography.Text
            type="secondary"
            className={styles.cardStepDetail}
            ellipsis={{ tooltip: record.current_step_detail }}
          >
            {record.current_step_detail}
          </Typography.Text>
        )}

        {renderProgress(record)}

        <div className={styles.cardMetrics}>
          <span>
            {t("RecommendationRunsHistory-Segments")}:{" "}
            {record.segments_generated || 0}
          </span>
          <span>
            {t("RecommendationRunsHistory-CommercialOpportunities")}:{" "}
            {record.commercial_opportunities_generated || 0}
          </span>
          <span>
            {t("RecommendationRunsHistory-Rewards")}: {record.rewards_generated || 0}
          </span>
          <span>
            {t("RecommendationRunsHistory-Promotions")}:{" "}
            {record.promotions_generated || 0}
          </span>
          <span>
            {t("RecommendationRunsHistory-Actions")}: {record.actions_generated || 0}
          </span>
        </div>

        {(record.started_at || record.finished_at) && (
          <div className={styles.cardDates}>
            {record.started_at && (
              <Typography.Text type="secondary">
                {t("RecommendationRunsHistory-StartedAt")}: {formatDate(record.started_at)}
              </Typography.Text>
            )}
            {record.finished_at && (
              <Typography.Text type="secondary">
                {t("RecommendationRunsHistory-FinishedAt")}:{" "}
                {formatDate(record.finished_at)}
              </Typography.Text>
            )}
          </div>
        )}

        {record.error_message && (
          <Typography.Paragraph
            className={styles.cardError}
            ellipsis={{
              rows: 2,
              expandable: true,
              symbol: t("RecommendationRun-ShowMore"),
              tooltip: record.error_message,
            }}
          >
            {record.error_message}
          </Typography.Paragraph>
        )}
      </article>
    );
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title={t("RecommendationRunsHistory-Title")}
        icon={<HistoryOutlined />}
        showCreateButton={false}
        additionalButtons={
          <>
            <Button
              type="primary"
              loading={startingRun}
              disabled={initialLoading || isActive}
              onClick={handleStartRecommendations}
            >
              {isActive
                ? t("NavBar-RecommendationsGenerating")
                : t("NavBar-GenerateRecommendations")}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => fetchHistory(actualPage)}>
              {t("Common-Refresh")}
            </Button>
          </>
        }
      />

      <Alert
        className={styles.info}
        type="info"
        showIcon
        message={t("RecommendationRunsHistory-Description")}
      />

      {isCompactList ? (
        <div className={styles.cardList}>
          {loading && runs.length === 0 ? (
            <div className={styles.cardListLoading}>
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className={styles.runCard}>
                  <Skeleton active paragraph={{ rows: 3 }} />
                </div>
              ))}
            </div>
          ) : (
            <>
              <List
                loading={loading}
                dataSource={runs}
                locale={{ emptyText: t("RecommendationRunsHistory-Empty") }}
                renderItem={(record) => (
                  <List.Item className={styles.cardListItem}>
                    {renderRunCard(record)}
                  </List.Item>
                )}
              />
              <div className={styles.cardPagination}>
                <Pagination {...paginationConfig} />
              </div>
            </>
          )}
        </div>
      ) : (
        <Table
          className={styles.table}
          rowKey="id"
          size="small"
          tableLayout="fixed"
          loading={loading}
          columns={columns}
          dataSource={runs}
          pagination={paginationConfig}
          locale={{
            emptyText: t("RecommendationRunsHistory-Empty"),
          }}
        />
      )}
    </div>
  );
};

export default RecommendationRunsHistory;
